"use strict";
// (C) 2021-2023 GoodData Corporation
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultSaveButton = exports.useSaveButtonProps = void 0;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var react_intl_1 = require("react-intl");
var sdk_ui_kit_1 = require("@gooddata/sdk-ui-kit");
var noop_1 = tslib_1.__importDefault(require("lodash/noop"));
var model_1 = require("../../../../../model");
var locales_1 = require("../../../../../locales");
var selectors_1 = require("../selectors");
/**
 * @internal
 */
function useSaveButtonProps() {
    var dispatch = model_1.useDashboardDispatch();
    var title = model_1.useDashboardSelector(model_1.selectDashboardTitle);
    var intl = react_intl_1.useIntl();
    var emptyTitle = intl.formatMessage({ id: "untitled" });
    // In some cases, when you click the save button (for example immediately after you insert an insight)
    // and the dashboard is still processing some related requests to this insight, it can take few seconds,
    // before the CMD.SAVE starts to process (as dashboard can process only 1 command at a time).
    // We want to show the loading indicator immediately and avoid any double-save race conditions,
    // so add a local state for this case.
    var _a = tslib_1.__read(react_1.useState(false), 2), optimisticIsSaving = _a[0], setOptimisticIsSaving = _a[1];
    var onSaveClick = react_1.useCallback(function () {
        setOptimisticIsSaving(true);
        model_1.dispatchAndWaitFor(dispatch, 
        // the || is intentional, we want to replace empty string as well
        model_1.saveDashboard(title || emptyTitle))
            .then(function () {
            setOptimisticIsSaving(false);
        })
            .catch(function () {
            setOptimisticIsSaving(false);
        });
    }, [dispatch, emptyTitle, title]);
    var isEditing = model_1.useDashboardSelector(model_1.selectIsInEditMode);
    var isSavingDashboard = model_1.useDashboardSelector(model_1.selectIsDashboardSaving);
    var arePermissionsEnabled = model_1.useDashboardSelector(model_1.selectEnableAnalyticalDashboardPermissions);
    var isPrivateDashboard = model_1.useDashboardSelector(selectors_1.selectIsPrivateDashboard);
    var isEmptyDashboard = !model_1.useDashboardSelector(model_1.selectLayoutHasAnalyticalWidgets); // we need at least one non-custom widget there
    var canSaveDashboard = model_1.useDashboardSelector(selectors_1.selectCanSaveDashboard);
    var isDashboardDirty = model_1.useDashboardSelector(model_1.selectIsDashboardDirty);
    var isSaving = isSavingDashboard || optimisticIsSaving;
    var isVisible = isEditing;
    var isEnabled = isDashboardDirty && !isEmptyDashboard && canSaveDashboard;
    var buttonValue = arePermissionsEnabled
        ? locales_1.messages.controlButtonsSaveValue
        : locales_1.messages.controlButtonsSaveAndPublishValue;
    var buttonTitle = locales_1.messages.controlButtonsSaveAndPublishTitle;
    if (isPrivateDashboard) {
        buttonTitle = locales_1.messages.controlButtonsSaveAsPrivateTitle;
    }
    if (!canSaveDashboard) {
        buttonTitle = locales_1.messages.controlButtonsSaveAndPublishNoChanges;
    }
    if (isEmptyDashboard) {
        buttonTitle = locales_1.messages.controlButtonsSaveAndPublishEmpty;
    }
    return {
        isVisible: isVisible,
        isEnabled: isEnabled,
        isSaving: isSaving,
        buttonValue: buttonValue,
        buttonTitle: buttonTitle,
        onSaveClick: onSaveClick,
    };
}
exports.useSaveButtonProps = useSaveButtonProps;
/**
 * @internal
 */
function DefaultSaveButton(_a) {
    var isVisible = _a.isVisible, isEnabled = _a.isEnabled, isSaving = _a.isSaving, buttonTitle = _a.buttonTitle, buttonValue = _a.buttonValue, onSaveClick = _a.onSaveClick;
    var intl = react_intl_1.useIntl();
    if (!isVisible) {
        return null;
    }
    if (isSaving) {
        // While save is in progress, the save button needs to be disabled and show 'Saving...'
        return (react_1.default.createElement(sdk_ui_kit_1.Button, { className: "gd-button-action s-saving_button", value: intl.formatMessage({ id: "controlButtons.saving.value" }), disabled: true, onClick: noop_1.default }));
    }
    return (react_1.default.createElement(sdk_ui_kit_1.BubbleHoverTrigger, null,
        react_1.default.createElement(sdk_ui_kit_1.Button, { className: "gd-button-action save-publish-button s-save_button", value: intl.formatMessage(buttonValue), onClick: onSaveClick, disabled: !isEnabled }),
        react_1.default.createElement(sdk_ui_kit_1.Bubble, { alignPoints: [{ align: "bc tr" }], arrowOffsets: { "bc tr": [10, 20] }, alignTo: ".save-publish-button" },
            react_1.default.createElement(react_intl_1.FormattedMessage, tslib_1.__assign({}, buttonTitle)))));
}
exports.DefaultSaveButton = DefaultSaveButton;
//# sourceMappingURL=DefaultSaveButton.js.map